/**
 * Services Australia API error message extraction (TAV-2590).
 *
 * The API deliberately stores and returns the COMPLETE vendor error string - status code, request
 * path and raw JSON body - because that is what makes a failure diagnosable after the fact
 * (`company_sah_claim_items.sa_push_error`, and `api.items[].error` / `api.warnings[].reason` in a
 * finalise response). That full string is not something to show a provider, e.g.:
 *
 *   Support at Home error - invoices /1000113317/item returned 400: {"code":400,"codeType":"DHSEIN",
 *   "message":"Item 1 - Care recipient 415115854 is not approved for SERV-0062 on Ongoing
 *   classification.","details":[{"code":"ZSSM_VALIDATION/010","message":"Item 1 - Care recipient ..."}]}
 *
 * This pulls out just the human-readable part:
 *
 *   Item 1 - Care recipient 415115854 is not approved for SERV-0062 on Ongoing classification.
 *
 * Parsing lives on the web side precisely so the backend keeps the raw string intact - nothing is
 * discarded, it is only presented differently.
 *
 * Two error body shapes exist and both are handled:
 *   - SaH application (`codeType: 'DHSEIN'`): a top-level `message`, usually mirrored in
 *     `details[].message`. Multiple `details` entries can carry several distinct validation
 *     failures for one item, so all distinct messages are kept and joined.
 *   - IBM API gateway (routing/header rejections, before the request reaches the SaH app):
 *     `{ httpCode, httpMessage, moreInformation }` with no `message` field at all.
 *
 * Anything unparseable falls through to the original string - never an empty or invented message,
 * because a raw error the provider can quote to support beats silently showing nothing.
 */

/**
 * Returns the distinct human-readable messages from a vendor error string, as a LIST.
 *
 * A list rather than a joined string because Services Australia can report several separate
 * validation failures for one item, and those belong on separate lines when displayed - running
 * them together on one line is what makes a multi-failure error unreadable. Callers that genuinely
 * need one line (a toast) use `parseSahApiError` below.
 *
 * Empty array only when there is nothing at all to show.
 */
export const parseSahApiErrors = (raw?: string | null): string[] => {
  if (!raw) return [];

  const jsonStart = raw.indexOf('{');
  if (jsonStart === -1) return [raw];

  try {
    // `unknown` rather than `any`: the parsed vendor body genuinely has no compile-time shape, and
    // `unknown` says so without switching type checking off - the `typeof message === 'string'`
    // filter below is what narrows it, so nothing downstream needs a cast.
    const body: Record<string, unknown> = JSON.parse(raw.slice(jsonStart));

    // Collect the top-level message plus every detail message, then dedupe. In the common case the
    // detail simply repeats the top-level message, so this collapses to a single entry; when SA
    // returns several distinct validation failures they all survive as separate entries.
    const detailMessages = Array.isArray(body?.details)
      ? (body.details as unknown[]).map((detail) => (detail as { message?: unknown } | null)?.message)
      : [];
    const messages = [body?.message, ...detailMessages].filter(
      (message: unknown): message is string => typeof message === 'string' && message.trim().length > 0,
    );
    const unique = [...new Set(messages.map((message) => message.trim()))];
    if (unique.length > 0) return unique;

    // Gateway shape: `moreInformation` is the descriptive one, `httpMessage` the terse fallback.
    if (typeof body?.moreInformation === 'string' && body.moreInformation.trim()) return [body.moreInformation];
    if (typeof body?.httpMessage === 'string' && body.httpMessage.trim()) return [body.httpMessage];

    return [raw];
  } catch {
    return [raw];
  }
};

/**
 * Single-line variant, for contexts that cannot render multiple lines - currently only the finalise
 * toast, whose `showToast(type, message)` signature takes a plain string. Prefer
 * `parseSahApiErrors` and render one message per line wherever the layout allows it.
 */
export const parseSahApiError = (raw?: string | null): string => parseSahApiErrors(raw).join(' · ');

/**
 * TAV-2601 / TAV-2602 — formats the Services Australia `Message[]` returned by a successful
 * rollover / elect-to-return call for display. `longText` is documented as duplicating
 * `shortText` when there is no long text, so it is only appended when it genuinely differs.
 * Every field on `Message` is optional (no `required` list in the vendor schema).
 */
export const formatSahMessages = (
  messages?: Array<{ shortText?: string; longText?: string } | null | undefined> | null,
): string[] => {
  if (!Array.isArray(messages)) return [];
  return messages
    .map((message) => {
      const short = message?.shortText?.trim();
      const long = message?.longText?.trim();
      if (short && long && long !== short) return `${short} — ${long}`;
      return short || long || '';
    })
    .filter((message) => message.length > 0);
};

/**
 * Worst severity across a `Message[]` — 'E' beats 'W' beats null (no messages, or only 'I').
 * A missing `type` is treated as non-fatal (`Message.type` is optional in the vendor schema).
 */
export const severityOf = (
  messages?: Array<{ type?: 'I' | 'W' | 'E' } | null | undefined> | null,
): 'E' | 'W' | null => {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  if (messages.some((message) => message?.type === 'E')) return 'E';
  if (messages.some((message) => message?.type === 'W')) return 'W';
  return null;
};

/**
 * The `reason_code` on a failed import, or null when the response carries none.
 *
 * Only a pre-flight refusal has one: `checkImportConnection` returns before any vendor call is made
 * and the controller reports the cause as `data.reason_code`. `rtkBaseQueryWrapper` puts the whole
 * response body on `ApiError.data`, which is what makes it readable here.
 */
export const readReasonCode = (value: unknown): string | null => {
  const data = (value as { data?: unknown } | undefined)?.data;
  if (!data || typeof data !== 'object' || !('reason_code' in data)) return null;
  const code = (data as { reason_code?: unknown }).reason_code;
  return typeof code === 'string' ? code : null;
};

export default parseSahApiErrors;
